import React, { useState } from 'react'
import Button from '../components/Button'

const CommunityPage = () => {
  const [activeTab, setActiveTab] = useState('conversations')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')

  // Mock data for community content
  const conversations = [
    {
      id: 1,
      title: "How I discovered a 50% growth stock using AI analysis",
      author: "TradingPro2024",
      avatar: "👨‍💼",
      date: "2 hours ago",
      likes: 24,
      comments: 8,
      category: "analysis",
      tags: ["growth", "AI", "screening"],
      preview: "I used the AI chat to analyze market trends and discovered a hidden gem that's been growing steadily..."
    },
    {
      id: 2,
      title: "My workflow for automated portfolio rebalancing",
      author: "QuantTrader",
      avatar: "👩‍💻",
      date: "1 day ago",
      likes: 42,
      comments: 15,
      category: "workflow",
      tags: ["automation", "portfolio", "rebalancing"],
      preview: "Here's my complete workflow setup that automatically rebalances my portfolio based on market conditions..."
    },
    {
      id: 3,
      title: "Technical analysis: AAPL breakout pattern confirmed",
      author: "ChartMaster",
      avatar: "📊",
      date: "3 days ago",
      likes: 67,
      comments: 23,
      category: "analysis",
      tags: ["technical", "breakout", "AAPL"],
      preview: "The AI copilot helped me identify a clear breakout pattern in Apple's chart. Here's what I found..."
    },
    {
      id: 4,
      title: "Screening for high-dividend yield stocks",
      author: "DividendHunter",
      avatar: "💰",
      date: "5 days ago",
      likes: 31,
      comments: 12,
      category: "screening",
      tags: ["dividend", "screening", "income"],
      preview: "I created a custom screener to find stocks with sustainable dividend yields above 4%..."
    }
  ]

  const workflows = [
    {
      id: 1,
      name: "Daily Market Scanner",
      author: "MarketWatcher",
      avatar: "👁️",
      date: "1 week ago",
      likes: 89,
      downloads: 156,
      category: "screening",
      description: "Automated workflow that scans the market daily for breakout opportunities",
      tags: ["daily", "scanner", "breakout"]
    },
    {
      id: 2,
      name: "Earnings Alert System",
      author: "EarningsTracker",
      avatar: "📈",
      date: "2 weeks ago",
      likes: 67,
      downloads: 98,
      category: "alerts",
      description: "Get notified when companies in your watchlist announce earnings",
      tags: ["earnings", "alerts", "notifications"]
    },
    {
      id: 3,
      name: "Risk Management Dashboard",
      author: "RiskManager",
      avatar: "🛡️",
      date: "3 weeks ago",
      likes: 45,
      downloads: 73,
      category: "risk",
      description: "Comprehensive risk assessment workflow for portfolio management",
      tags: ["risk", "portfolio", "management"]
    }
  ]

  const categories = [
    { id: 'all', label: 'All', icon: '📋' },
    { id: 'analysis', label: 'Analysis', icon: '📊' },
    { id: 'workflow', label: 'Workflows', icon: '🔄' },
    { id: 'screening', label: 'Screening', icon: '🔍' },
    { id: 'alerts', label: 'Alerts', icon: '🔔' },
    { id: 'risk', label: 'Risk', icon: '🛡️' }
  ]

  const filteredContent = () => {
    let content = activeTab === 'conversations' ? conversations : workflows
    let filtered = content

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(item => 
        item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.preview?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.category === selectedCategory)
    }

    return filtered
  }

  const renderConversationCard = (conversation) => (
    <div key={conversation.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start space-x-4">
        <div className="text-2xl">{conversation.avatar}</div>
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <h3 className="font-semibold text-gray-800">{conversation.title}</h3>
            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
              {conversation.category}
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-3">{conversation.preview}</p>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <span>by {conversation.author}</span>
              <span>{conversation.date}</span>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1">
                <span>👍</span>
                <span className="text-sm">{conversation.likes}</span>
              </div>
              <div className="flex items-center space-x-1">
                <span>💬</span>
                <span className="text-sm">{conversation.comments}</span>
              </div>
              <Button className="bg-primary-600 hover:bg-primary-700 text-white text-sm">
                View
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {conversation.tags.map((tag, index) => (
              <span key={index} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                #{tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  )

  const renderWorkflowCard = (workflow) => (
    <div key={workflow.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start space-x-4">
        <div className="text-2xl">{workflow.avatar}</div>
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <h3 className="font-semibold text-gray-800">{workflow.name}</h3>
            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
              {workflow.category}
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-3">{workflow.description}</p>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <span>by {workflow.author}</span>
              <span>{workflow.date}</span>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1">
                <span>👍</span>
                <span className="text-sm">{workflow.likes}</span>
              </div>
              <div className="flex items-center space-x-1">
                <span>⬇️</span>
                <span className="text-sm">{workflow.downloads}</span>
              </div>
              <Button className="bg-primary-600 hover:bg-primary-700 text-white text-sm">
                Import
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {workflow.tags.map((tag, index) => (
              <span key={index} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                #{tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className="flex h-full bg-gray-50">
      {/* Main Content */}
      <div className="flex-1 p-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Community</h1>
            <p className="text-gray-600">Share and discover conversations and workflows from the TraderBro community</p>
          </div>

          {/* Search and Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search Bar */}
              <div className="flex-1">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search conversations, workflows, or tags..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    🔍
                  </span>
                </div>
              </div>

              {/* Category Filter */}
              <div className="flex space-x-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedCategory === category.id
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span className="mr-1">{category.icon}</span>
                    {category.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 mb-6">
            <button
              onClick={() => setActiveTab('conversations')}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                activeTab === 'conversations'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              💬 Conversations ({conversations.length})
            </button>
            <button
              onClick={() => setActiveTab('workflows')}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                activeTab === 'workflows'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              🔄 Workflows ({workflows.length})
            </button>
          </div>

          {/* Content Grid */}
          <div className="space-y-4">
            {filteredContent().length === 0 ? (
              <div className="text-center py-16">
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No results found</h3>
                <p className="text-gray-500">Try adjusting your search or filters</p>
              </div>
            ) : (
              filteredContent().map(item => 
                activeTab === 'conversations' 
                  ? renderConversationCard(item)
                  : renderWorkflowCard(item)
              )
            )}
          </div>
        </div>
      </div>

      {/* Right Sidebar */}
      <div className="w-80 bg-white border-l border-gray-200 p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Share Your Content</h3>
          <div className="space-y-3">
            <Button className="w-full bg-primary-600 hover:bg-primary-700 text-white">
              📝 Share Conversation
            </Button>
            <Button className="w-full bg-green-600 hover:bg-green-700 text-white">
              🔄 Share Workflow
            </Button>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Trending Topics</h3>
          <div className="space-y-2">
            {['#AIanalysis', '#breakout', '#screening', '#automation', '#riskmanagement'].map((tag, index) => (
              <div key={index} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded cursor-pointer">
                <span className="text-sm text-gray-700">{tag}</span>
                <span className="text-xs text-gray-500">{Math.floor(Math.random() * 100) + 10} posts</span>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-gray-200 pt-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Contributors</h3>
          <div className="space-y-3">
            {[
              { name: 'TradingPro2024', posts: 45, avatar: '👨‍💼' },
              { name: 'QuantTrader', posts: 38, avatar: '👩‍💻' },
              { name: 'ChartMaster', posts: 32, avatar: '📊' },
              { name: 'MarketWatcher', posts: 28, avatar: '👁️' }
            ].map((contributor, index) => (
              <div key={index} className="flex items-center space-x-3">
                <span className="text-xl">{contributor.avatar}</span>
                <div className="flex-1">
                  <div className="font-medium text-sm">{contributor.name}</div>
                  <div className="text-xs text-gray-500">{contributor.posts} posts</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CommunityPage 